import React, { useEffect, useRef } from 'react';
import JSONEditor, { ExpandOptions, JSONEditorOptions, OnNodeNameParams } from 'jsoneditor';
import 'jsoneditor/dist/jsoneditor.css';
import './json-editor.css';
import { isEqual } from '../../utils';

interface RawJsonEditorProps extends JSONEditorOptions {
	text?: string;
	onChangeText?: (text: string) => void;
	onReady?: (jsoneditor: JSONEditor) => void;
	onNodeName?: ((nodeName: OnNodeNameParams) => string | undefined) | undefined;
	onExpand?: (expandParams: ExpandOptions) => void | undefined;
}

const RawJsonEditor: React.FC<RawJsonEditorProps> = (props) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const jsoneditorRef = useRef<JSONEditor | null>(null);

	// Ref Proxies
	const onNodeNameRef = useRef(props.onNodeName);
	const onChangeTextRef = useRef(props.onChangeText);
	const onExpandRef = useRef(props.onExpand);

	// 1. TRACK INTERACTION: Keep track of whether the update originated from the user
	const isUserInteracting = useRef(false);

	useEffect(() => {
		onNodeNameRef.current = props.onNodeName;
		onChangeTextRef.current = props.onChangeText;
		onExpandRef.current = props.onExpand;
	});

	const schemaRef = useRef<any>(JSON.parse(JSON.stringify(props.schema)));
	const schemaRefsRef = useRef<any>(
		!!props.schemaRefs ? JSON.parse(JSON.stringify(props.schemaRefs)) : null
	);

	useEffect(() => {
		const options: JSONEditorOptions = {
			...props,
			indentation: 4,
			mainMenuBar: false,
			navigationBar: false,

			onNodeName: (params) => {
				return onNodeNameRef.current?.({
					...params,
					path: params.path as string[],
					type: params.type as 'object' | 'array',
				});
			},
			onChangeText: (text) => {
				// 2. FLAG INTERACTION: We (the editor) are causing this change.
				isUserInteracting.current = true;
				onChangeTextRef.current?.(text);
			},
			onExpand: (params) => {
				onExpandRef.current?.(params);
			},
		};

		// @ts-ignore
		delete options.text;
		// @ts-ignore
		delete options.onReady;

		if (containerRef.current) {
			try {
				jsoneditorRef.current = new JSONEditor(containerRef.current, options);
			} catch (error) {
				console.error('Failed to create JSONEditor:', error);
			}

			jsoneditorRef.current.setMode(props.mode || 'tree');

			if ('text' in props && props.text) {
				try {
					jsoneditorRef.current.setText(props.text);
				} catch (error) {
					console.error('Failed to set JSON text:', error);
				}
			}

			if (props.onReady) {
				props.onReady(jsoneditorRef.current);
			}
		}

		return () => {
			if (jsoneditorRef.current) {
				jsoneditorRef.current.destroy();
			}
		};
	}, []);

	// Handle Updates
	useEffect(() => {
		if (jsoneditorRef.current) {
			if ('text' in props && props.text !== undefined) {
				// 3. CHECK FLAG: If the user just typed this, IGNORE the prop update.
				if (isUserInteracting.current) {
					// Reset the flag. We have successfully ignored the echo from React.
					isUserInteracting.current = false;
				} else {
					// This update is EXTERNAL (e.g. "Format" button, or parent state change unrelated to typing)
					try {
						const currentText = jsoneditorRef.current.getText();
						// Only update if actually different to minimize DOM thrashing
						if (currentText !== props.text) {
							jsoneditorRef.current.updateText(props.text);
						}
					} catch (error) {
						console.error('Failed to update JSON text:', error);
					}
				}
			}

			const schemaChanged = !isEqual(props.schema, schemaRef.current);
			const schemaRefsChanged = !isEqual(
				props.schemaRefs,
				schemaRefsRef.current
			);

			if (schemaChanged || schemaRefsChanged) {
				schemaRef.current = JSON.parse(JSON.stringify(props.schema));
				schemaRefsRef.current = !!props.schemaRefs
					? JSON.parse(JSON.stringify(props.schemaRefs))
					: null;
				jsoneditorRef.current.setSchema(props.schema, props.schemaRefs);
			}

			if (props.mode) {
				try {
					jsoneditorRef.current.setMode(props.mode);
				} catch (error) {
					console.error('Failed to update JSONEditor mode:', error);
				}
			}
		}
	}, [props.text, props.schema, props.schemaRefs, props.mode]);

	return <div className='jsoneditor-react-container' ref={containerRef} />;
};

export default RawJsonEditor;
